/**
 * ShareEventEvidenceDialog — generates a tokenised public link for an
 * Event Video clip so the user can hand evidence to an insurer or
 * customer without exposing internal accounts.
 *
 * Backend contract (already implemented in routes_media.py):
 *   POST   /api/media/event-videos/{video_id}/share-links
 *   GET    /api/media/event-videos/{video_id}/share-links
 *   DELETE /api/media/event-videos/share-links/{share_id}
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Textarea } from '../ui/textarea';
import { Loader2, Copy, Check, ExternalLink, Ban, Eye, Calendar, ShieldCheck, Mail, Phone, AlertTriangle, CheckCircle, Download } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { useLiveTracking } from '../../contexts/LiveTrackingContext';
import { resolveTimezone, formatDT } from '../../utils/timezone';
import { getPreferredMapType } from '../../utils/mapPreferences';
import { ConfirmActionDialog } from '../shared/ConfirmActionDialog';

const EXPIRY_PRESETS = [
  { label: '1 day', days: 1 },
  { label: '7 days', days: 7 },
  { label: '30 days', days: 30 },
  { label: '60 days', days: 60 },
  { label: 'Custom', days: null },
];

// Hard cap mirrors the S3 lifecycle policy: chat/videos/downloads
// objects auto-delete 90 days from upload. Links that would outlive
// the underlying object are pointless (recipient gets a 404), so we
// cap the picker accordingly. The backend enforces the same cap +
// the tighter per-clip "video already aged X days" rule.
const SHARE_LINK_MAX_LIFETIME_DAYS = 90;

const isoForDaysFromNow = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString();
};

const isoForCustomLocal = (localValue) => {
  if (!localValue) return null;
  // `localValue` from <input type="datetime-local"> is in browser-local time without timezone.
  const d = new Date(localValue);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

const minCustomDateTimeLocal = () => {
  const d = new Date(Date.now() + 60_000);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Compute the latest the share link can possibly expire, given that the
// underlying S3 object will be auto-deleted 90 days from the clip's
// recording date. Returns ``null`` if we can't pin a recording date
// (legacy clip without media_start_time_utc) — in that case the only
// cap is the absolute 90-day-from-now wall.
const computeClipRetentionEndIso = (video) => {
  const anchor = video?.media_start_time_utc || video?.event_time || video?.created_at;
  if (!anchor) return null;
  const d = new Date(anchor);
  if (Number.isNaN(d.getTime())) return null;
  d.setDate(d.getDate() + SHARE_LINK_MAX_LIFETIME_DAYS);
  return d.toISOString();
};

// Combined cap = MIN(now + 90d, clip recording date + 90d). Returns an
// ISO string. Used to gate preset buttons + clamp the custom picker.
const computeEffectiveCapIso = (video) => {
  const absCap = new Date(Date.now() + SHARE_LINK_MAX_LIFETIME_DAYS * 86400_000);
  const clipCapIso = computeClipRetentionEndIso(video);
  if (!clipCapIso) return absCap.toISOString();
  const clipCap = new Date(clipCapIso);
  return clipCap < absCap ? clipCapIso : absCap.toISOString();
};

const isoToDateTimeLocal = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const ShareEventEvidenceDialog = ({ open, onOpenChange, video, device }) => {
  const { api } = useAuth();
  const { isNotificationChannelActive, hasOSMMaps, hasGoogleMaps } = useFeatureFlags();
  const { mapType, darkModeMap, showPOI } = useLiveTracking();
  const [preset, setPreset] = useState(7);   // days, or null for Custom
  const [customLocal, setCustomLocal] = useState('');
  const [note, setNote] = useState('');
  const [allowDownload, setAllowDownload] = useState(false);
  const [creating, setCreating] = useState(false);
  const [sending, setSending] = useState(false);
  const [links, setLinks] = useState([]);
  const [loadingLinks, setLoadingLinks] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [deliveryMethod, setDeliveryMethod] = useState('link');
  const [deliveryRecipient, setDeliveryRecipient] = useState('');
  const [deliveryMessage, setDeliveryMessage] = useState('');
  const [deliveryAlert, setDeliveryAlert] = useState(null);
  const deviceTz = resolveTimezone({ device });
  const canEmail = isNotificationChannelActive('notification_email');
  const canSMS = isNotificationChannelActive('notification_sms');
  const effectiveMapType = getPreferredMapType({ contextMapType: mapType, hasOSMMaps, hasGoogleMaps });
  const deliveryTabs = useMemo(() => {
    const tabs = [{ id: 'link', label: 'Copy Link', icon: Copy }];
    if (canEmail) tabs.push({ id: 'email', label: 'Email', icon: Mail });
    if (canSMS) tabs.push({ id: 'sms', label: 'SMS', icon: Phone });
    return tabs;
  }, [canEmail, canSMS]);

  // S3-retention-aware cap: the share link can't outlive the underlying
  // S3 object (which auto-deletes 90 days from the clip's recording).
  // Re-computed only when the dialog opens for a different clip.
  // deps list the exact fields computeEffectiveCapIso() reads; the whole `video`
  // object is intentionally excluded so this doesn't recompute on parent re-renders
  // that hand down a new object identity for the same clip.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const effectiveCapIso = useMemo(() => computeEffectiveCapIso(video), [video?.id, video?.media_start_time_utc, video?.event_time, video?.created_at]);
  const effectiveCapMs = useMemo(() => new Date(effectiveCapIso).getTime(), [effectiveCapIso]);
  const daysRemaining = useMemo(() => Math.max(0, Math.floor((effectiveCapMs - Date.now()) / 86400_000)), [effectiveCapMs]);

  // A preset (e.g. "30 days") is disabled if applying it would push the
  // expiry past the effective cap. Custom is never disabled — we clamp
  // the date picker instead.
  const isPresetDisabled = (days) => {
    if (days === null) return false;
    return Date.now() + days * 86400_000 > effectiveCapMs + 60_000;
  };

  const presetIsCustom = preset === null;

  // If the user reopens the dialog on a clip whose remaining window is
  // smaller than their previously-selected preset, snap down to the
  // largest allowed preset so the UI never sits in an invalid state.
  useEffect(() => {
    if (presetIsCustom) return;
    if (isPresetDisabled(preset)) {
      const fallback = [...EXPIRY_PRESETS]
        .filter(p => p.days !== null && !isPresetDisabled(p.days))
        .sort((a, b) => b.days - a.days)[0];
      setPreset(fallback ? fallback.days : null);
      if (!fallback) setCustomLocal(isoToDateTimeLocal(effectiveCapIso));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveCapMs]);

  // Always render the public URL using the BROWSER'S current origin so
  // it matches whichever domain the operator is currently logged into
  // (e.g. ``https://livetrack.ensue.com.au/evidence/<token>``). The
  // backend stores the token, the frontend owns the URL — this means
  // the link "just works" across preview / staging / production without
  // any backend env-var configuration.
  const buildPublicUrl = (token) => {
    if (!token) return '';
    if (typeof window === 'undefined' || !window.location?.origin) return `/evidence/${token}`;
    return `${window.location.origin}/evidence/${token}`;
  };

  const publicUrlForLink = (link) => link?.url || buildPublicUrl(link?.token);

  const computedExpiryIso = useMemo(() => {
    if (presetIsCustom) return isoForCustomLocal(customLocal);
    return isoForDaysFromNow(preset);
  }, [preset, presetIsCustom, customLocal]);

  // The share-link backend has two parallel routes depending on the clip
  // type — both honour the same payload schema, only the path + id-field
  // differ. Remote Media downloads are passed through `buildDownloadVideo`
  // which sets `event_type='remote_download'` and stores the download id
  // under `download_id` (NOT `id`).
  const isRemoteDownload = video?.event_type === 'remote_download' || !!video?.download_id;
  const clipId = isRemoteDownload ? video?.download_id : video?.id;
  const shareBasePath = isRemoteDownload
    ? `/media/remote-downloads/${clipId}/share-links`
    : `/media/event-videos/${clipId}/share-links`;

  // Load existing links whenever the dialog opens
  useEffect(() => {
    if (!open || !clipId) return;
    let cancelled = false;
    setLoadingLinks(true);
    api.get(shareBasePath)
      .then(res => { if (!cancelled) setLinks(Array.isArray(res.data?.links) ? res.data.links : []); })
      .catch(err => {
        if (cancelled) return;
        const detail = err?.response?.data?.detail;
        if (err?.response?.status === 403) {
          // Privilege not enabled — caller already gated the button so this is rare
          toast.error(detail || 'You do not have permission to share evidence links.');
        }
      })
      .finally(() => { if (!cancelled) setLoadingLinks(false); });
    return () => { cancelled = true; };
  }, [open, clipId, shareBasePath, api]);

  useEffect(() => {
    if (!open) {
      setDeliveryMethod('link');
      setDeliveryRecipient('');
      setDeliveryMessage('');
      setDeliveryAlert(null);
      setAllowDownload(false);
    }
  }, [open]);

  const sendShareLink = async (link) => {
    if (!link?.id) throw new Error('Share link is not ready yet');
    const res = await api.post(`/media/event-videos/share-links/${link.id}/send`, {
      method: deliveryMethod,
      recipient: deliveryRecipient.trim(),
      message: deliveryMessage.trim(),
    });
    const sentAt = res.data?.sent_at || new Date().toISOString();
    setLinks(prev => prev.map(l => l.id === link.id ? {
      ...l,
      send_count: (Number(l.send_count) || 0) + 1,
      last_sent_at: sentAt,
      last_sent_method: deliveryMethod,
      delivery_channels: Array.from(new Set([...(l.delivery_channels || []), deliveryMethod])),
    } : l));
    return res.data;
  };

  const handleCreate = async () => {
    if (!computedExpiryIso) {
      toast.error('Please choose a valid expiry date in the future.');
      return;
    }
    if (deliveryMethod !== 'link' && !deliveryRecipient.trim()) {
      toast.error(deliveryMethod === 'email' ? 'Please enter a recipient email address.' : 'Please enter a recipient phone number.');
      return;
    }
    setCreating(true);
    setDeliveryAlert(null);
    try {
      const res = await api.post(shareBasePath, {
        expires_at: computedExpiryIso,
        note: note.trim(),
        // Bake the operator's current Live Tracking map prefs into the link
        // so the recipient sees the same provider/theme/POI mix.
        map_type: effectiveMapType,
        dark_mode_map: Boolean(darkModeMap),
        show_poi: showPOI !== false,
        // Operator opt-in: let the recipient download the original clip.
        allow_download: allowDownload,
      });
      const newLink = res.data;
      setLinks(prev => [newLink, ...prev]);
      if (deliveryMethod === 'link') {
        try {
          await navigator.clipboard.writeText(publicUrlForLink(newLink));
          toast.success('Evidence link generated and copied to clipboard');
        } catch {
          toast.success('Evidence link generated');
        }
      } else {
        setSending(true);
        try {
          await sendShareLink(newLink);
          const channelLabel = deliveryMethod === 'email' ? 'email' : 'SMS';
          setDeliveryAlert({ success: true, message: `Evidence link sent via ${channelLabel} to ${deliveryRecipient.trim()}` });
          toast.success(`Evidence link generated and sent via ${channelLabel}`);
          setDeliveryRecipient('');
          setDeliveryMessage('');
        } catch (sendErr) {
          const detail = sendErr?.response?.data?.detail || sendErr?.message || 'Failed to send evidence link';
          setDeliveryAlert({ success: false, message: `Link generated, but delivery failed: ${detail}` });
          toast.error(`Link generated, but delivery failed: ${detail}`);
        } finally {
          setSending(false);
        }
      }
      setNote('');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to create evidence link';
      toast.error(detail);
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = async (link) => {
    try {
      await navigator.clipboard.writeText(publicUrlForLink(link));
      setCopiedId(link.id);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      toast.error('Clipboard copy failed — long-press to copy manually');
    }
  };

  const [revokeTarget, setRevokeTarget] = useState(null);

  const handleRevoke = (link) => setRevokeTarget(link);

  const doRevoke = async () => {
    const link = revokeTarget;
    if (!link) return;
    try {
      await api.delete(`/media/event-videos/share-links/${link.id}`);
      setLinks(prev => prev.map(l => l.id === link.id ? { ...l, revoked_at: new Date().toISOString(), is_active: false } : l));
      toast.success('Link revoked');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to revoke link';
      toast.error(detail);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-slate-900 border-slate-800 text-slate-100 max-h-[92vh] overflow-y-auto overflow-x-hidden" data-testid="share-evidence-dialog">
        <DialogHeader className="pr-8">
          <DialogTitle className="flex items-center gap-2 text-slate-100">
            <ShieldCheck className="w-5 h-5 text-emerald-400" />
            Share Event Evidence Link
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Generates a tokenised public link to this clip + GPS trail. The recipient does not need a login.
            Every view is audited for compliance.
          </DialogDescription>
        </DialogHeader>

        {/* New link form */}
        <div className="space-y-3 border border-slate-800 rounded-md p-3 bg-slate-950/50">
          <div>
            <Label className="text-xs text-slate-300">Expires after</Label>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {EXPIRY_PRESETS.map(p => {
                const disabled = isPresetDisabled(p.days);
                return (
                  <button
                    key={p.label}
                    type="button"
                    onClick={() => !disabled && setPreset(p.days)}
                    disabled={disabled}
                    title={disabled ? `Exceeds the ${daysRemaining}-day retention window for this clip` : undefined}
                    className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
                      disabled
                        ? 'bg-slate-900 border-slate-800 text-slate-600 cursor-not-allowed'
                        : preset === p.days
                          ? 'bg-emerald-600 border-emerald-500 text-white'
                          : 'bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700'
                    }`}
                    data-testid={`share-expiry-preset-${p.label.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
            {presetIsCustom && (
              <div className="mt-2">
                <Input
                  type="datetime-local"
                  value={customLocal}
                  min={minCustomDateTimeLocal()}
                  max={isoToDateTimeLocal(effectiveCapIso)}
                  onChange={(e) => setCustomLocal(e.target.value)}
                  className="bg-[#172031] border-slate-700 text-slate-100 text-sm"
                  data-testid="share-expiry-custom-input"
                />
                <div className="text-[10px] text-slate-500 mt-1">
                  In your local timezone ({deviceTz}). Maximum {daysRemaining} day{daysRemaining === 1 ? '' : 's'} from now (clip auto-deletes from storage after that).
                </div>
              </div>
            )}
            <div className="text-[10px] text-amber-300/80 mt-1.5 flex items-center gap-1">
              <ShieldCheck className="w-3 h-3 flex-shrink-0" />
              <span>
                {daysRemaining > 0
                  ? `90-day media retention: this clip will auto-delete from storage in ~${daysRemaining} day${daysRemaining === 1 ? '' : 's'}.`
                  : "This clip is past the 90-day retention window — sharing may fail."}
              </span>
            </div>
          </div>

          <div>
            <Label htmlFor="share-note" className="text-xs text-slate-300">Note (optional)</Label>
            <Input
              id="share-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Acme Insurance — claim #12345"
              maxLength={500}
              className="bg-[#172031] border-slate-700 text-slate-100 text-sm mt-1"
              data-testid="share-note-input"
            />
            <div className="text-[10px] text-slate-500 mt-1">Stored with the link's audit trail so you can recall who you shared it with.</div>
          </div>

          {/* Operator opt-in: allow the recipient to download the original clip.
              OFF by default — every download is logged (timestamp + IP). */}
          <div className="flex items-start gap-2 rounded-md border border-slate-800 bg-slate-900/40 p-2.5">
            <Checkbox
              id="share-allow-download"
              checked={allowDownload}
              onCheckedChange={(c) => setAllowDownload(Boolean(c))}
              className="mt-0.5 border-slate-600 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
              data-testid="share-allow-download-checkbox"
            />
            <div className="space-y-0.5">
              <Label htmlFor="share-allow-download" className="text-xs text-slate-300 cursor-pointer flex items-center gap-1.5">
                <Download className="w-3.5 h-3.5" /> Allow recipient to download the original clip
              </Label>
              <div className="text-[10px] text-slate-500">Off by default. Every download is logged with timestamp + IP for chain-of-custody.</div>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs text-slate-300">Delivery</Label>
            <div className="flex flex-wrap gap-1 border border-slate-800 rounded-md bg-slate-950/60 p-1" data-testid="share-delivery-tabs">
              {deliveryTabs.map(t => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => { setDeliveryMethod(t.id); setDeliveryAlert(null); }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded transition-colors ${deliveryMethod === t.id ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-slate-100'}`}
                  data-testid={`share-delivery-tab-${t.id}`}
                >
                  <t.icon className="w-3.5 h-3.5" />
                  {t.label}
                </button>
              ))}
            </div>

            {deliveryMethod === 'email' && (
              <div className="space-y-2" data-testid="share-email-delivery-panel">
                <div className="space-y-1.5">
                  <Label htmlFor="share-email-recipient" className="text-xs text-slate-300">Recipient Email</Label>
                  <Input
                    id="share-email-recipient"
                    type="email"
                    value={deliveryRecipient}
                    onChange={(e) => setDeliveryRecipient(e.target.value)}
                    placeholder="email@example.com"
                    className="bg-[#172031] border-slate-700 text-slate-100 text-sm"
                    data-testid="share-email-recipient-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="share-email-message" className="text-xs text-slate-300">Message (optional)</Label>
                  <Textarea
                    id="share-email-message"
                    value={deliveryMessage}
                    onChange={(e) => setDeliveryMessage(e.target.value)}
                    placeholder="Add a note for the recipient..."
                    rows={2}
                    className="bg-[#172031] border-slate-700 text-slate-100 text-sm"
                    data-testid="share-email-message-input"
                  />
                </div>
              </div>
            )}

            {deliveryMethod === 'sms' && (
              <div className="space-y-2" data-testid="share-sms-delivery-panel">
                <div className="space-y-1.5">
                  <Label htmlFor="share-sms-recipient" className="text-xs text-slate-300">Recipient Phone Number</Label>
                  <Input
                    id="share-sms-recipient"
                    type="tel"
                    value={deliveryRecipient}
                    onChange={(e) => setDeliveryRecipient(e.target.value)}
                    placeholder="+61 400 000 000"
                    className="bg-[#172031] border-slate-700 text-slate-100 text-sm"
                    data-testid="share-sms-recipient-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="share-sms-message" className="text-xs text-slate-300">Message (optional)</Label>
                  <Textarea
                    id="share-sms-message"
                    value={deliveryMessage}
                    onChange={(e) => setDeliveryMessage(e.target.value)}
                    placeholder="Short note..."
                    rows={2}
                    className="bg-[#172031] border-slate-700 text-slate-100 text-sm"
                    data-testid="share-sms-message-input"
                  />
                </div>
              </div>
            )}

            {deliveryAlert && (
              <div
                className={`p-2.5 rounded-md border text-xs ${deliveryAlert.success ? 'bg-emerald-950/35 border-emerald-800 text-emerald-100' : 'bg-red-950/35 border-red-800 text-red-100'}`}
                data-testid="share-delivery-alert"
              >
                <div className="flex items-start gap-2">
                  {deliveryAlert.success ? <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5" /> : <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5" />}
                  <span>{deliveryAlert.message}</span>
                </div>
              </div>
            )}
          </div>

          <Button
            onClick={handleCreate}
            disabled={creating || sending || !computedExpiryIso}
            className="w-full bg-emerald-600 hover:bg-emerald-500 text-white"
            data-testid="share-generate-link-button"
          >
            {(creating || sending) ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : deliveryMethod === 'email' ? <Mail className="w-4 h-4 mr-2" /> : deliveryMethod === 'sms' ? <Phone className="w-4 h-4 mr-2" /> : <ExternalLink className="w-4 h-4 mr-2" />}
            {deliveryMethod === 'email' ? 'Generate Link & Send Email' : deliveryMethod === 'sms' ? 'Generate Link & Send SMS' : 'Generate Link & Copy'}
          </Button>
        </div>

        {/* Existing links list */}
        <div className="space-y-2 min-w-0 max-w-full">
          <div className="flex items-center justify-between">
            <Label className="text-xs text-slate-300">Existing share links for this clip</Label>
            {loadingLinks && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
          </div>
          {!loadingLinks && links.length === 0 && (
            <div className="text-xs text-slate-500 italic py-2" data-testid="share-no-links">No links shared yet for this clip.</div>
          )}
          <div className="space-y-2 max-h-[260px] overflow-y-auto pr-1">
            {links.map(link => {
              const expired = link.expires_at && new Date(link.expires_at) < new Date();
              const revoked = Boolean(link.revoked_at);
              const inactive = expired || revoked;
              return (
                <div
                  key={link.id}
                  className={`border rounded-md p-2.5 text-xs ${inactive ? 'border-slate-800 bg-slate-900/40 opacity-60' : 'border-slate-700 bg-slate-800/50'}`}
                  data-testid={`share-link-row-${link.id}`}
                >
                  <div className="flex items-center justify-between gap-2 min-w-0">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mb-1">
                        {revoked ? (
                          <Badge variant="destructive" className="text-[9px] h-4">Revoked</Badge>
                        ) : expired ? (
                          <Badge variant="secondary" className="text-[9px] h-4">Expired</Badge>
                        ) : (
                          <Badge className="bg-emerald-600 text-[9px] h-4">Active</Badge>
                        )}
                        <span className="flex items-center gap-1 text-slate-400">
                          <Eye className="w-3 h-3" /> {link.view_count} {link.view_count === 1 ? 'view' : 'views'}
                        </span>
                        {link.allow_download && (
                          <span className="flex items-center gap-1 text-emerald-400" data-testid={`share-link-download-count-${link.id}`} title="Downloads enabled for this link">
                            <Download className="w-3 h-3" /> {link.download_count || 0} {(link.download_count || 0) === 1 ? 'download' : 'downloads'}
                          </span>
                        )}
                        <span className="flex items-center gap-1 text-slate-400">
                          <Calendar className="w-3 h-3" /> expires {formatDT(link.expires_at, { device })}
                        </span>
                      </div>
                      {link.note && <div className="text-slate-300 truncate" title={link.note}>{link.note}</div>}
                      <div className="text-slate-500 truncate font-mono text-[10px]" title={publicUrlForLink(link)}>{publicUrlForLink(link)}</div>
                      {link.send_count > 0 && (
                        <div className="text-slate-500 text-[10px] mt-0.5" data-testid={`share-link-send-count-${link.id}`}>
                          Sent {link.send_count} time{link.send_count === 1 ? '' : 's'}{link.last_sent_method ? ` · last via ${link.last_sent_method === 'sms' ? 'SMS' : 'email'}` : ''}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {!inactive && (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-slate-300 hover:bg-slate-700"
                          onClick={() => handleCopy(link)}
                          title="Copy link"
                          data-testid={`share-link-copy-${link.id}`}
                        >
                          {copiedId === link.id ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                        </Button>
                      )}
                      {!inactive && (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-red-300 hover:bg-red-900/40"
                          onClick={() => handleRevoke(link)}
                          title="Revoke"
                          data-testid={`share-link-revoke-${link.id}`}
                        >
                          <Ban className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <ConfirmActionDialog
          open={!!revokeTarget}
          onOpenChange={(o) => { if (!o) setRevokeTarget(null); }}
          title="Revoke Share Link?"
          message={<>Anyone using this link will lose access immediately.{revokeTarget?.note ? <><br />Note: {revokeTarget.note}</> : null}</>}
          confirmLabel="Revoke"
          onConfirm={doRevoke}
          testPrefix="revoke-share-link"
        />
      </DialogContent>
    </Dialog>
  );
};

export default ShareEventEvidenceDialog;
